import React from "react";
import { SimpleFormLabel, SimpleFormTextField } from "components/dialogs/SimpleFormDialog";
import { Box, Button, DialogActions, DialogContent, DialogTitle, SelectChangeEvent, Typography, Select, MenuItem, Checkbox, FormControlLabel, Paper } from "@mui/material";
import exportAsImage from "../../../utils/exportAsImage";
import { WidgetBaseProps } from "../WidgetContainer";
import ResponsiveDialog from "components/dialogs/ResponsiveDialog";
import { useTranslation } from 'react-i18next';
import { useTheme } from "@mui/material/styles";
import { enqueueSnackbar } from "notistack";

interface WidgetImageDownloadDialogOptions extends WidgetBaseProps {
  onClose: () => void;
}

export default function WidgetImageDownloadDialog(props: WidgetImageDownloadDialogOptions): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const [imageFileName, setImageFileName] = React.useState<string>(props.widget.name ?? "widget-image");
  const [imageFileType, setImageFileType] = React.useState<string>("png");
  const [useTransparency, setUseTransparency] = React.useState<boolean>(false);
  const [isExporting, setIsExporting] = React.useState<boolean>(false);

  const handleExportImage = (success:boolean) => {
    setIsExporting(false);
    if (success) {
      enqueueSnackbar(i18n.t("widgets.download-image-success"), { variant: "success" });
      props.onClose();
    } else {
      enqueueSnackbar(i18n.t("widgets.download-image-failure"), { variant: "error", persist: true });
    }
  }

  return (
    <ResponsiveDialog
      maxWidth="sm"
      open={true}
      onClose={props.onClose}
    >
      <DialogTitle>
        <Typography variant="h4">
          {i18n.t("widgets.download-image-dialog")}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: theme.palette.background.default, p: 1 }} >
        <Paper sx={{display:"grid", gridTemplateColumns: "auto 130px", columnGap: 2, p: 2}}>
          <Box>
            <SimpleFormLabel>{i18n.t("widgets.export-filename")}</SimpleFormLabel>
            <SimpleFormTextField value={imageFileName} onChange={(e) => setImageFileName(e.target.value)}/>
          </Box>
          <Box>
            <SimpleFormLabel>{i18n.t("widgets.export-format")}</SimpleFormLabel>
            <Select
              fullWidth
              variant="outlined"
              label={i18n.t("widgets.export-format")}
              defaultValue="png"
              onChange={(e: SelectChangeEvent) => {
                setImageFileType(e.target.value);
                setUseTransparency(false);
              }}
              value={imageFileType}
            >
              <MenuItem value="jpeg">JPG</MenuItem>
              <MenuItem value="png">PNG</MenuItem>
            </Select>
          </Box>
          {/* IF file type supports a transparency layer, show transparency toggle option */}
          {(imageFileType === "png") &&
            <Box>
              <FormControlLabel 
                label={i18n.t("widgets.download-image-transparency")} 
                control={<Checkbox checked={useTransparency} onChange={(e) => setUseTransparency(e.target.checked)} />} />
            </Box>
          }
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={props.onClose}
          variant="contained"
          color="secondary">
          {i18n.t("cancel")}
        </Button>
        <Button
          onClick={() => { 
            setIsExporting(true);
            setTimeout(() => {
              exportAsImage(
                props.contentRef.current,
                imageFileName,
                imageFileType,
                useTransparency,
                theme,
                handleExportImage,
                props.contentPath
              );
            }, 100);
          }}
          loading={isExporting}
          variant="contained"
        >
          {i18n.t("widgets.download")}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  )
}
